/* eslint-disable camelcase */
/* eslint-disable react/display-name */
/**
 * @file BloodPressureView.jsx
 * @author Daniel Aguirre
 * @description BloodPressure View Page
 */

// Dependencies
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import DashboardCard from 'components/DashboardCard';
import DashboardTitle from 'components/Charts/DashboardTitle';
import ProgressBar from 'components/Charts/ProgressBar';
import { useHistory } from 'react-router';
import styleGuideColors from 'commons/styleGuideColors';
import EmptyChart from 'components/EmptyChart';
import useTruncateAndFormat from 'hook/useTruncateAndFormat';

/**
 * BloodPressure View
 * @param { Object } dashboardStaffActions - DashboardStaff Actions.
 * @return { Node } React node.
 */

function BloodPressureView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const [chartData, setChartData] = useState(null);

  const { getBloodPressure } = dashboardStaffActions;

  const onGetData = (_, resp) => {
    const data = [];
    const colors = {
      'Muy Alta': styleGuideColors.alertError,
      Alta: styleGuideColors.graphYellow,
      Aceptable: styleGuideColors.baxterBrandTeal,
      Baja: styleGuideColors.alertError
    };
    let total = 0;

    if (resp?.data) {
      resp.data.forEach(({ cantidad }) => {
        total += cantidad;
      });
      resp.data.forEach(({ cantidad, clasificacion }) => {
        const percent = (cantidad * 100) / total;
        const item = {
          title: cantidad,
          subtitle: clasificacion,
          percent: useTruncateAndFormat(percent, 2),
          color: colors[clasificacion]
        };
        if (clasificacion === 'Muy Alta') {
          data[0] = item;
        } else if (clasificacion === 'Alta') {
          data[1] = item;
        } else if (clasificacion === 'Aceptable') {
          data[2] = item;
        } else if (clasificacion === 'Baja') {
          data[3] = item;
        }
      });
      setChartData(data);
    } else {
      setChartData([]);
    }
  };

  useEffect(() => {
    getBloodPressure({ clinicId }, onGetData);
  }, [translate, clinicId]);

  const handleDetailClick = (index) => {
    // Get data by index and send by url param to detail list
    const { subtitle: classification, title: cantidad } = chartData[index];
    history.push(
      `/main/dashboard-detail/bloodPressure/${classification}/list/${cantidad}`
    );
  };

  return (
    <DashboardCard id="F20" loading={!chartData}>
      <div className="flex flex-wrap">
        <DashboardTitle
          title={translate('dashboardStaff.bloodPressureControl')}
        />
        {chartData?.length === 0 && <EmptyChart />}
        <div className="px-2 w-full">
          {chartData?.map(({ color, title, subtitle, percent }, index) => (
            <div onClick={() => handleDetailClick(index)} key={index}>
              <ProgressBar
                color={color}
                title={title}
                subtitle={subtitle}
                percent={percent}
              />
            </div>
          ))}
        </div>
      </div>
    </DashboardCard>
  );
}

/**
 * Component definition of proptypes.
 */
BloodPressureView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getBloodPressure: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default BloodPressureView;
